/**
 * Oxbo-M client — enterprise master data (well register + catalogs). Same
 * contract as Oxbo-Z: Oxbo-W consults & caches, never writes masters.
 *
 * Base URL is VITE_OXBO_M_URL (m.dev.oxbo.energy by default). In dev / standalone
 * mode MSW intercepts these calls and serves the mock register + catalogs, so
 * the app runs with no live backend.
 *
 *   GET /v1/wells                       — list the register
 *   GET /v1/wells/{well_id}             — one well
 *   GET /v1/categories/{key}/resolved   — an effective-dated catalog
 */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { WellOut, ResolvedCatalogOut } from "./types";
import { toWellView, type WellView } from "./mappers";
import { getDevToken } from "./devAuth";
import { getProdToken } from "./prodAuth";
import { asArray } from "../lib/asArray";

/** Prefer the real-login prod token, fall back to the dev-minted token. */
function getAuthToken(): string | null {
  return getProdToken() ?? getDevToken();
}

/** Server-side filters the M contract accepts on /v1/wells. */
export interface WellListParams {
  status?: string;
  active_only?: boolean;
}

const M_BASE_URL = import.meta.env.VITE_OXBO_M_URL ?? "https://m.dev.oxbo.energy";

export const mApi = createApi({
  reducerPath: "mApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${M_BASE_URL}/v1`,
    prepareHeaders: (headers) => {
      const token = getAuthToken();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  // Enterprise master data changes rarely — keep it cached generously.
  keepUnusedDataFor: 300,
  tagTypes: ["Well", "Catalog"],
  endpoints: (builder) => ({
    getWells: builder.query<WellView[], WellListParams | void>({
      query: (params) => ({ url: "/wells", params: params ?? { active_only: false } }),
      transformResponse: (raw: unknown) => asArray<WellOut>(raw).map(toWellView),
      providesTags: (result) =>
        result
          ? [
              ...result.map((w) => ({ type: "Well" as const, id: w.id })),
              { type: "Well" as const, id: "LIST" },
            ]
          : [{ type: "Well" as const, id: "LIST" }],
    }),
    getWell: builder.query<WellView, string>({
      query: (id) => `/wells/${id}`,
      transformResponse: (raw: WellOut) => toWellView(raw),
      providesTags: (_r, _e, id) => [{ type: "Well", id }],
    }),
    // Resolved catalog — accounts (CoA), tubulars, formations, activity codes.
    // W's CatalogPicker binds these for the design screens.
    getResolvedCatalog: builder.query<
      ResolvedCatalogOut,
      { categoryKey: string; asOf?: string; siteRegionKey?: string }
    >({
      query: ({ categoryKey, asOf, siteRegionKey }) => ({
        url: `/categories/${categoryKey}/resolved`,
        params: {
          ...(asOf ? { as_of: asOf } : {}),
          ...(siteRegionKey ? { site_region_key: siteRegionKey } : {}),
        },
      }),
      providesTags: (_r, _e, { categoryKey }) => [{ type: "Catalog", id: categoryKey }],
    }),
  }),
});

export const { useGetWellsQuery, useGetWellQuery, useGetResolvedCatalogQuery } = mApi;
